/* eslint-disable camelcase */
import _, { get } from 'lodash';
import { createSlice } from '@reduxjs/toolkit';

import { ALWAYS_SHOWN_TOOLS, NON_ANNOTATIONS_TOOLS } from 'app/components/Viewer/constants';
import extractAcquisitionDataFromDicomData from 'app/utils/dicom/extractAcquisitionDataFromDicomData';
import convertDisplayableImageDataToDataImage from 'app/utils/image/convertDisplayableImageDataToDataImage';
import isWorkListImage from 'app/utils/isWorkListImage';
import {
  DEFAULT_ANATOMIC_REGION_WITH_SPECIE,
  anatomicRegionToString,
  getAnatomicRegionFromString,
  getDefaultAnatomicRegion,
} from 'app/utils/xrayRegions';
import {
  AcquisitionConstants,
  DetectorInfo as AcquiredDetectorInfo,
  DisplayableImageData,
  Feedbacks,
  GeneratorErrorInfo,
  ImageAnnotations,
  ImageDisplayableMetadata,
  ImageMetadata,
  ImageOrigin,
  Viewport,
} from 'app/interfaces/Image';
import { AnyAnnotation, ToolAnnotations } from 'app/CornerstoneTools/types';
import { Patient } from 'app/interfaces/Patient';
import { DEFAULT_SPECIE, SpeciesType } from 'app/constants/species';
import { DicomData } from 'app/interfaces/Dicom';
import { IntlShape } from 'react-intl';
import { ImageSaveState, ViewerImage, ViewerStudy } from 'app/components/Viewer/types';
import { ConfigurableToolsKey } from 'app/adapters/ImageRenderer/ConfigurableToolsKeys';
import { convertRectToCropHandles } from 'app/CornerstoneTools/CropTool';
import { NegatoMode, NEGATO_MODE_INFO } from 'app/components/ToolsBar/NegatoButton';
import { ViewerState } from 'app/components/Viewer';
import { convertPredictionsToVHSAnnotation } from 'app/utils/predictions/convertPredictionsToVHSAnnotation';
import { convertPredictionsToNorbergOlssonAnnotation } from 'app/utils/predictions/convertPredictionsToNorbergOlssonAnnotation';
import produce, { current, isDraft, original } from 'immer';
import { PRECOMPUTED_TOOLS } from '../ToolsBar/PRECOMPUTED_TOOLS';
import { VISIBLE_TOOLS } from 'app/CornerstoneTools/constants';
import formatXRayImageViewerState from 'app/components/Viewer/formatXRayImageViewerState';
import { getDicomDataValue } from 'app/utils/dicom/DicomData';
import { ToolsStates } from 'app/adapters/ImageRenderer/ConfigurableToolsOptions';
import { makeAcquisitionDetailsFromImage } from 'app/components/Viewer/makeAcquisitionDetailsFromImage';
import { LoadedImage } from 'app/interfaces/ImageLoader';
import { computeRealSizePixelSpacingFromActualSpacing } from 'app/CornerstoneTools/RealSizeMeasurementCalibrationTool';
import { onRealSizeMeasurementConfigurationToggle } from 'app/components/Dropzone/RealSizeMeasurementCalibrationConfiguration';
import { AllPredictions } from 'app/interfaces/Predictions';
import { ProcessingKind } from 'app/interfaces/IImageProcessor';
import { createImageToolsList } from 'app/adapters/ImageRenderer/DefaultCornerstoneToolsInitialization';
import adaptStoredViewportAndAnnotationsToLoadedImage from 'app/utils/image/adaptStoredViewportAndAnnotationsToLoadedImage';
import { PACSError } from 'app/interfaces/PACSCommunication';
import { isImportedImage } from 'app/utils/isImportedImage';
import { convertCropAnnotationsToCropRect } from 'app/utils/image/convertCropAnnotationsToCropRect';
import { DataImage } from 'app/interfaces/DataImage';
import { FilterDescription } from 'app/components/Viewer/components/RightPanel/FilterParametrization';
import { ProcessingOptionsProvider } from 'app/hooks/useProcessingOptionsProvider';

const DEFAULT_ACQUISITION_CONSTANTS = {
  thickness: 0,
  kV: 0,
  mA: 0,
  s: 0,
};
Object.freeze(DEFAULT_ACQUISITION_CONSTANTS);

function getImageById(
  { studies }: Pick<ViewerState, 'studies'>,
  imageId: string
): { image: ViewerImage; study: ViewerStudy } | undefined {
  for (const study of Object.values(studies)) {
    for (const image of study.images) {
      if (image._id === imageId) return { image, study };
    }
  }
}

/**
 * Check that state contains only one study and return it.
 */
function getOnlyStudy({ studies }: ViewerState): ViewerStudy | undefined {
  if (Object.values(studies).length !== 1) return undefined;
  return Object.values(studies)[0];
}

const selectImage = ({ studies, selectedNegatoView }: ViewerState, imageId: string) => {
  let previousNegatoView;
  let previousDisplayedImage: ViewerImage;
  const imageToSelect = getImageById({ studies }, imageId)?.image;
  const isCurrentImageWorklistImage = isWorkListImage(imageToSelect);

  for (const study of Object.values(studies)) {
    for (const image of study.images) {
      image.isSelected = image._id === imageId;
      // Don't try to put worklist image into negatoview
      if (isCurrentImageWorklistImage) continue;

      // Here we trade places of negato view on selection
      if (image.isSelected) {
        if (image.negatoView !== selectedNegatoView) {
          previousNegatoView = image.negatoView;
        }
        image.negatoView = selectedNegatoView;
      } else if (image.negatoView === selectedNegatoView) {
        image.negatoView = undefined;
        previousDisplayedImage = image;
      }
    }
  }
  if (previousDisplayedImage && previousNegatoView !== undefined) {
    previousDisplayedImage.negatoView = previousNegatoView;
  }
};

const selectNegatoViewInImages = (draftImages: ViewerImage[], selectedNegatoView: number = 0) => {
  _.forEach(draftImages, (draftImage) => {
    draftImage.isSelected = draftImage.negatoView === selectedNegatoView;
  });
};

const initWorkListImage = ({
  studyId,
  imageId,
  anatomicRegion,
  acquisitionConstants = DEFAULT_ACQUISITION_CONSTANTS,
  isSelected = false,
  isReloading = false,
}: {
  imageId: string;
  studyId: string;
  anatomicRegion: string;
  acquisitionConstants?: AcquisitionConstants;
  isSelected?: boolean;
  isReloading?: boolean;
}) => ({
  _id: imageId,
  study: studyId,
  isSelected,
  anatomicRegion,
  acquisitionConstants,
  isImageLoading: false,
  displayableImage: undefined as any,
  loadError: undefined as any,
  predictions: undefined as any,
  isPredictionsLoading: false,
  feedback: undefined as any,
  inferenceError: undefined as any,
  origin: 'worklist' as const,
  saveState: {},
  reloadedState: isReloading
    ? {
        acquisitionInfo: formatXRayImageMainState({ anatomicRegion, acquisitionConstants }),
      }
    : {},
});

function injectPixelSpacingIntoDisplayableImage(
  draftImage: ViewerImage,
  image: DisplayableImageData
) {
  const { rowPixelSpacing, columnPixelSpacing } = draftImage?.initialDataImage ?? {};
  return { ...image, rowPixelSpacing, columnPixelSpacing };
}

function copyPixelSpacingFromOtherImage(
  draftImage: DisplayableImageData,
  otherImage: DisplayableImageData
) {
  const { rowPixelSpacing, columnPixelSpacing } = otherImage ?? {};
  if (!rowPixelSpacing || !columnPixelSpacing || !draftImage) return;
  draftImage.rowPixelSpacing = rowPixelSpacing;
  draftImage.columnPixelSpacing = columnPixelSpacing;
}

function getNextEmptyNegatoViewPanel(
  images: ViewerImage[],
  currentNegatoViewPanel: number,
  viewCount: number = 1
) {
  let nextNegatoViewPanel = currentNegatoViewPanel;
  while (++nextNegatoViewPanel < viewCount) {
    if (!_.find(images, { negatoView: nextNegatoViewPanel })) return nextNegatoViewPanel;
  }
  return undefined;
}

const hasMovedHandlesInTool = (imageAnnotation: ToolAnnotations) => {
  for (const k in imageAnnotation) {
    if (_.some(imageAnnotation[k].handles, 'hasMoved')) return true;
  }
};

const computePrecomputedAnnotations = (image: ViewerImage) => {
  const { displayableImage, predictions, annotations: imageAnnotations } = image;
  let annotations: ImageAnnotations = undefined;
  if (!displayableImage || !predictions) return undefined;
  if ('vhs' in predictions && !hasMovedHandlesInTool(imageAnnotations?.VHS)) {
    const VHSAnnotations = convertPredictionsToVHSAnnotation(predictions.vhs, displayableImage);
    if (VHSAnnotations) {
      VHSAnnotations.uuid = 'computed_uuid';
      annotations = _.merge(annotations, { VHS: { computed_uuid: VHSAnnotations } });
    }
  }
  if ('norberg_olsson' in predictions && !hasMovedHandlesInTool(imageAnnotations?.NorbergOlsson)) {
    const NorbergOlssonAnnotation = convertPredictionsToNorbergOlssonAnnotation(
      predictions.norberg_olsson,
      displayableImage
    );
    if (NorbergOlssonAnnotation) {
      NorbergOlssonAnnotation.uuid = 'computed_uuid';

      annotations = _.merge(annotations, {
        NorbergOlsson: { computed_uuid: NorbergOlssonAnnotation },
      });
    }
  }
  return annotations;
};

function checkForChange(
  initialState: ViewerState,
  currentState: ViewerState,
  studyId: string,
  imageId: string,
  propertyPath: string
) {
  if (!isDraft(currentState)) return true;
  return (
    _.get(_.find(currentState.studies[studyId].images, { _id: imageId }), propertyPath) !==
    _.get(_.find(initialState.studies[studyId].images, { _id: imageId }), propertyPath)
  );
}

function checkForImageChange(
  initialState: ViewerState,
  currentState: ViewerState,
  studyId: string,
  imageId: string,
  propertyPath: string
) {
  return checkForChange(initialState, currentState, studyId, imageId, propertyPath);
}

function handleComputedToolsActivation(image: ViewerImage, newToolsState: ToolsStates) {
  PRECOMPUTED_TOOLS.some((toolName) => {
    if (!newToolsState[toolName]) return false;

    const isSwitchToVisibleState = newToolsState[toolName].state in VISIBLE_TOOLS;
    if (!isSwitchToVisibleState) return false;

    const isToolWithoutAnnotations = _.isEmpty(image.annotations?.[toolName]);

    if (isToolWithoutAnnotations) return false;

    return image.toolsList[toolName].state !== newToolsState[toolName].state;
  });

  // We are switching to visible state and have no precomputed tools, we try to inject one.
  const precomputedAnnotation = computePrecomputedAnnotations(image);
  if (!precomputedAnnotation) return;
  image.annotations = _.merge(image.annotations, precomputedAnnotation);
}

enum DisplayState {
  ALREADY_DISPLAYED,
  DISPLAYED,
  NOT_DISPLAYABLE,
  NO_FREE_VIEW,
}
function displayImageInFreeView(draftState: ViewerState, imageId: string) {
  const images = _.flatten(_.map(draftState.studies, 'images'));
  const image = getImageById(draftState, imageId)?.image;
  if (!image || isWorkListImage(image)) {
    return DisplayState.NOT_DISPLAYABLE;
  }
  if (image.negatoView !== undefined) {
    return DisplayState.ALREADY_DISPLAYED;
  }

  const { viewCount = 1 } = NEGATO_MODE_INFO[draftState.negatoMode as NegatoMode];
  const nextEmptyNegatoViewPanel = getNextEmptyNegatoViewPanel(images, 0, viewCount);

  if (nextEmptyNegatoViewPanel === undefined) {
    return DisplayState.NO_FREE_VIEW;
  }

  image.negatoView = nextEmptyNegatoViewPanel;
  return DisplayState.DISPLAYED;
}

function applyPixelSpacingFromRealSizeMeasurement(image: ViewerImage) {
  const realSizeMeasurement = Object.values(
    image?.annotations?.RealSizeMeasurementCalibration ?? {}
  )[0];
  if (!realSizeMeasurement) return;
  const { rowPixelSpacing = 1, columnPixelSpacing = 1 } = image?.initialDataImage ?? {};

  const newPixelSpacing = computeRealSizePixelSpacingFromActualSpacing(
    { rowPixelSpacing, columnPixelSpacing },
    realSizeMeasurement
  );
  if (!newPixelSpacing) {
    image.annotations.RealSizeMeasurementCalibration = undefined;
    return;
  }
  image.displayableImage.columnPixelSpacing = newPixelSpacing.columnPixelSpacing;
  image.displayableImage.rowPixelSpacing = newPixelSpacing.rowPixelSpacing;
}

const ACQUISITION_STATE_KEYS_NO_RENAME = [
  'acquisitionConstants',
  'anatomicRegion',
  'detectorInfo',
  'acquisitionTime',
  'feedback',
  'image_metadata',
  'operators',
];
const ALL_ACQUISITION_STATE_KEYS = ACQUISITION_STATE_KEYS_NO_RENAME;

export const formatXRayImageMainState = (
  image: Partial<ViewerImage>
): ImageSaveState['acquisitionInfo'] =>
  _.omitBy(_.pick(image, ACQUISITION_STATE_KEYS_NO_RENAME), _.isUndefined);

export const formatRawImageSaveState = (
  image: ViewerImage,
  imageIndex: number,
  patient: Patient
): ImageSaveState['rawImage'] => ({
  dataImage: image.initialDataImage,
  acquisitionDetails: makeAcquisitionDetailsFromImage(patient, image, imageIndex),
  dicomData: image.dicomData,
});

const RAW_IMAGE_IMAGE_STATE_KEYS = [
  'initialDataImage',
  'dicomData',
  'acquisitionTime',
  'acquisitionConstants',
  'anatomicRegion',
  'detectorInfo',
];
const updateSavedState = (initialState: ViewerState, draftState: ViewerState) => {
  for (const studyId of Object.keys(draftState.studies)) {
    const study = draftState.studies[studyId];
    for (const image of study.images) {
      const imageId = image._id;
      const hasMainStateChanged = _.some(ALL_ACQUISITION_STATE_KEYS, (key) =>
        checkForImageChange(initialState, draftState, studyId, imageId, key)
      );
      if (hasMainStateChanged) {
        image.saveState.acquisitionInfo = formatXRayImageMainState(image);
      }
      const hasViewerStateChanged = _.some(['annotations', 'viewport'], (key) =>
        checkForImageChange(initialState, draftState, studyId, imageId, key)
      );
      if (hasViewerStateChanged) {
        image.saveState.viewer = formatXRayImageViewerState(image);
      }
      const hasRawStateChanged =
        image.needRawSave &&
        _.some(RAW_IMAGE_IMAGE_STATE_KEYS, (key) =>
          checkForImageChange(initialState, draftState, studyId, imageId, key)
        );
      if (hasRawStateChanged || initialState.studies[studyId]?.patient !== study.patient) {
        const imageIndex = _.findIndex(study.images, { _id: imageId });
        image.saveState.rawImage = formatRawImageSaveState(image, imageIndex, study.patient);
      }
    }
  }
};

function syncPixelSpacingWithReferences(initialState: ViewerState, draftState: ViewerState) {
  _.forEach(_.flatten(_.map(draftState.studies, 'images')), (draftImage) => {
    if (!draftImage.displayableImage || !draftImage.initialDataImage) return;

    const imageCalibrationReference = draftImage?.image_metadata?.realSizeMeasurementImageReference;
    if (!imageCalibrationReference) return;

    const isNotSyncWithReference = _.some(
      ['displayableImage.rowPixelSpacing', 'displayableImage.columnPixelSpacing'],
      (key) =>
        _.get(draftImage, key) !==
        _.get(getImageById(draftState, imageCalibrationReference)?.image, key)
    );

    if (!isNotSyncWithReference) return;

    copyPixelSpacingFromOtherImage(
      draftImage.displayableImage,
      getImageById(draftState, imageCalibrationReference)?.image.displayableImage
    );
  });
}

function syncAnatomicRegionsToSpecies(initialState: ViewerState, currentState: ViewerState) {
  for (const study of Object.values(currentState.studies)) {
    const specie = study?.patient?.specie ?? DEFAULT_SPECIE;
    _.forEach(study?.images, (draftImage) => {
      if (!draftImage.anatomicRegion) return;
      if (draftImage.anatomicRegion.startsWith(specie)) return;

      draftImage.anatomicRegion = anatomicRegionToString({
        ...getAnatomicRegionFromString(draftImage.anatomicRegion),
        specie,
      });
    });
  }
}

const isImageFromAnotherStudy = (
  draftState: ViewerState,
  imageId: string,
  dicomData: DicomData,
  { isAIOnly }: { isAIOnly: boolean }
) => {
  const study = getOnlyStudy(draftState);
  if (!study) return false;
  const images = study.images;
  const image = getImageById(draftState, imageId)?.image;
  if (_.isEmpty(images)) return false;

  // We include all images if are not imported or if we are in AIOnly mode
  if (!(isAIOnly || isImportedImage(image))) return false;

  const currentImageStudyInstanceID = getDicomDataValue(dicomData, 'StudyInstanceUID');
  if (!currentImageStudyInstanceID) return false;

  const existingImportedImagesStudyInstanceID = _.reject(
    _.map(images, (image) => getDicomDataValue(image.dicomData, 'StudyInstanceUID')),
    _.isUndefined
  );
  const isAllStudyInstanceIDMatchCurrent = _.every(
    existingImportedImagesStudyInstanceID,
    (studyInstanceID) => studyInstanceID === currentImageStudyInstanceID
  );
  return !isAllStudyInstanceIDMatchCurrent;
};

function updateImageToolsForRealSizeMeasurementCalibration(
  state: ViewerState,
  image: ViewerImage,
  isEnabled?: boolean
) {
  isEnabled =
    isEnabled ??
    (image.toolsList.RealSizeMeasurementCalibration.state !== 'active' ||
      !image.isRealSizeMeasurementCalibration);
  image.isRealSizeMeasurementCalibration = isEnabled;

  // In case realsize measurement calibration is enabled and no specific state was requested, if it is not active, we activate it instead of disabling it

  if (!isEnabled) {
    image.disabledTools = undefined;
    _.merge(image.toolsList, image.realSizeMeasurementHiddenAnnotations);
    image.realSizeMeasurementHiddenAnnotations = undefined;
    image.toolsList.RealSizeMeasurementCalibration.state = 'disabled';
    return;
  } else if (!image.realSizeMeasurementHiddenAnnotations) {
    // When switching for the first time to real size measurement calibration, we need to save the current state of the annotations
    // In orde to restore it later.
    const annotationsToolsName = _.difference(Object.keys(image.toolsList), [
      ...NON_ANNOTATIONS_TOOLS,
      'RealSizeMeasurementCalibration',
    ]);
    image.disabledTools = annotationsToolsName;

    const realSizeMeasurementHiddenAnnotations = _.mapValues(
      _.pick(image.toolsList, annotationsToolsName),
      ({ state }) => ({ state })
    );
    image.realSizeMeasurementHiddenAnnotations = realSizeMeasurementHiddenAnnotations;

    const disabledAnnotationsStates = _.fromPairs(
      annotationsToolsName.map((toolName) => [toolName, { state: 'disabled' }])
    );
    _.merge(image.toolsList, disabledAnnotationsStates);
    image.pendingAnnotations = {
      RealSizeMeasurementCalibration: image.annotations?.RealSizeMeasurementCalibration,
    };
  }

  onRealSizeMeasurementConfigurationToggle(
    image.isRealSizeMeasurementCalibration,
    {
      ...state.commonToolsList,
      ...image.toolsList,
    },
    (updateFn: (toolsList: ToolsStates) => void) => updateFn(image.toolsList),
    (updateFn: (toolsList: ToolsStates) => void) => updateFn(state.commonToolsList)
  );
}

function checkMatchingAnatomicRegionForConstants(
  { mainRegion, subRegion, view }: ReturnType<typeof getAnatomicRegionFromString>,
  image: ViewerImage,
  allowAcquiredImage: boolean = false
) {
  if (!allowAcquiredImage && !isWorkListImage(image)) return false;
  if (!image.anatomicRegion) return false;

  const imageAnatomicRegion = getAnatomicRegionFromString(image.anatomicRegion);
  return (
    imageAnatomicRegion.view === view &&
    ((subRegion && imageAnatomicRegion.subRegion === subRegion) ||
      (!subRegion && imageAnatomicRegion.mainRegion === mainRegion))
  );
}

function findMatchingAnatomicRegionForConstants(
  anatomicRegionDescription: ReturnType<typeof getAnatomicRegionFromString>,
  images: ViewerImage[]
) {
  const worklistAcquisitionConstants = _.find(images, (image) =>
    checkMatchingAnatomicRegionForConstants(anatomicRegionDescription, image)
  )?.acquisitionConstants;
  if (worklistAcquisitionConstants) return worklistAcquisitionConstants;

  return _.filter(images, (image) =>
    checkMatchingAnatomicRegionForConstants(anatomicRegionDescription, image, true)
  )
    .sort((a, b) => {
      const aTime =
        a.acquisitionTime instanceof Date
          ? a.acquisitionTime.getTime()
          : Number(a.acquisitionTime) || 0;
      const bTime =
        b.acquisitionTime instanceof Date
          ? b.acquisitionTime.getTime()
          : Number(b.acquisitionTime) || 0;
      return aTime - bTime;
    })
    .at(-1)?.acquisitionConstants;
}

export function injectStudyInState(
  draftState: ViewerState,
  study: Partial<Omit<ViewerStudy, 'images'>>
) {
  const {
    _id,
    patient = { specie: DEFAULT_SPECIE } as Patient,
    creationDate = new Date(),
    pms_id = undefined,
    requested_procedure_id = undefined,
    study_instance_uid = undefined,
    scheduled_procedure_step_id = undefined,
    accession_number = undefined,
    comment = '',
    isCommentDirty = false,
  } = study;

  const saveState = _.cloneDeep({
    _id,
    patient,
    report: { comment, isCommentDirty },
  });

  draftState.studies[study._id] = {
    _id,
    patient,
    creationDate,
    pms_id,
    requested_procedure_id,
    study_instance_uid,
    scheduled_procedure_step_id,
    accession_number,
    comment,
    isCommentDirty,
    saveState,
    images: [],
  };
}

/**
 * Images reducer to be used with useImmerReducer
 */
const viewerSlice = createSlice({
  name: 'viewer',
  initialState: {} as ViewerState,
  reducers: {
    loadStudy(draftState, action: { payload: Partial<Omit<ViewerStudy, 'images'>> }) {
      injectStudyInState(draftState, action.payload);
    },
    updatePatient(
      draftState,
      action: { payload: { studyId: string; patient: Patient; isSaved?: boolean } }
    ) {
      const { studyId, patient, isSaved } = action.payload;
      const study = draftState.studies[studyId];
      if (!study) return;
      study.patient = patient;
      if (isSaved) {
        study.saveState.patient = patient;
      }
    },
    updateAnatomicRegion(
      draftState,
      action: { payload: { imageId: string; anatomicRegion: string } }
    ) {
      const { imageId, anatomicRegion } = action.payload;
      const { image: draftImage, study } = getImageById(draftState, imageId) ?? {};
      if (!draftImage) return;

      if (isWorkListImage(draftImage)) {
        const anatomicRegionDescription = getAnatomicRegionFromString(anatomicRegion);
        const matchingAcquisitionConstants = findMatchingAnatomicRegionForConstants(
          anatomicRegionDescription,
          study.images
        );
        if (matchingAcquisitionConstants) {
          draftImage.acquisitionConstants = matchingAcquisitionConstants;
        } else {
          draftImage.acquisitionConstants.kV = 0;
        }
      }
      draftImage.anatomicRegion = anatomicRegion;
    },
    updateOperators(draftState, action: { payload: { imageId: string; operators: string[] } }) {
      const { imageId, operators } = action.payload;
      const image = getImageById(draftState, imageId)?.image;
      if (image) image.operators = operators;
    },
    addWorkListImage(
      draftState,
      action: { payload: { imageId: string; anatomicRegion?: string } }
    ) {
      const { imageId, anatomicRegion = DEFAULT_ANATOMIC_REGION_WITH_SPECIE } = action.payload;
      const anatomicRegionDescription = getAnatomicRegionFromString(anatomicRegion);
      const study = getOnlyStudy(draftState);
      if (!study) return;

      const studyImages = study.images;
      const matchingAcquisitionConstants = findMatchingAnatomicRegionForConstants(
        anatomicRegionDescription,
        studyImages
      );

      studyImages.push(
        initWorkListImage({
          studyId: study._id,
          imageId,
          anatomicRegion,
          acquisitionConstants: matchingAcquisitionConstants,
        })
      );
      selectImage(draftState, imageId);
    },
    redoImage(draftState, action: { payload: { imageId: string; imageIdToCopy: string } }) {
      const { imageId, imageIdToCopy } = action.payload;
      const { image: imageToCopy, study } = getImageById(draftState, imageIdToCopy) ?? {};
      if (!imageToCopy) return;

      const imageIndex = _.findIndex(study.images, { _id: imageIdToCopy });
      study.images.splice(
        imageIndex + 1,
        0,
        initWorkListImage({
          studyId: study._id,
          imageId,
          anatomicRegion: imageToCopy.anatomicRegion,
          acquisitionConstants: imageToCopy.acquisitionConstants,
        })
      );
      selectImage(draftState, imageId);
    },
    loadWorkListImage(
      draftState,
      action: {
        payload: {
          studyId: string;
          imageId: string;
          anatomicRegion: string;
          acquisitionConstants: AcquisitionConstants;
        };
      }
    ) {
      const { imageId, anatomicRegion, acquisitionConstants, studyId } = action.payload;
      const study = draftState.studies[studyId];
      const isFirstImage = _.isEmpty(_.flatten(_.map(draftState.studies, 'images')));
      study.images.push(
        initWorkListImage({
          studyId: study._id,
          imageId,
          anatomicRegion,
          acquisitionConstants,
          isReloading: true,
        })
      );
      if (isFirstImage) selectImage(draftState, imageId);
    },
    receiveAcquiredImage(
      draftState,
      action: {
        payload: {
          imageId: string;
          acquiredImage: DataImage;
          detectorInfo: AcquiredDetectorInfo;
          patient: Patient;
          intl: IntlShape;
          generatorError?: GeneratorErrorInfo;
        };
      }
    ) {
      const { imageId, acquiredImage, detectorInfo, patient, intl, generatorError } =
        action.payload;
      const acquisitionTime = new Date();
      const study = getOnlyStudy(draftState);
      if (!study) return;
      const existingImage = _.find(study.images, { _id: imageId });

      const { acquisitionConstants, anatomicRegion = getDefaultAnatomicRegion(patient?.specie) } =
        existingImage ?? {};
      const finalImage = _.merge(existingImage, {
        _id: imageId,
        study: study._id,
        origin: 'acquisition',
        anatomicRegion,
        detectorInfo,
        acquisitionTime,
        toolsList: createImageToolsList(() => intl),
        photometric_interpretation: 'MONOCHROME',
        acquisitionConstants,
        image_metadata: {
          PACS: { isSync: false },
          generatorError,
          has_anti_scattering_grid: study.defaultHasAntiScatteringGrid ?? false,
        },
        PACS: { isSyncOngoing: false },
        saveState: {},
        needRawSave: true,
      });
      if (!existingImage) {
        study.images.push(finalImage);
      }

      const [rowPixelSpacing, columnPixelSpacing] = detectorInfo?.pixelSpacing ?? [];
      finalImage.initialDataImage = {
        ...acquiredImage,
        rowPixelSpacing,
        columnPixelSpacing,
      };

      const isPMSWorklistSudy = !!study.pms_id;
      if (isPMSWorklistSudy) {
        finalImage.dicomData = _.merge(finalImage.dicomData ?? {}, {
          AccessionNumber: String(study.pms_id),
        });
      }

      const isPACSWorklistStudy = !!(
        study.requested_procedure_id ||
        study.study_instance_uid ||
        study.scheduled_procedure_step_id ||
        study.accession_number
      );
      if (isPACSWorklistStudy) {
        finalImage.dicomData = _.merge(
          finalImage.dicomData ?? {},
          _.pickBy({
            RequestedProcedureID: String(study.requested_procedure_id),
            StudyInstanceUID: String(study.study_instance_uid),
            ScheduledProcedureStepID: String(study.scheduled_procedure_step_id),
            AccessionNumber: String(study.accession_number),
          })
        );
      }

      displayImageInFreeView(draftState, imageId);
      selectImage(draftState, imageId);
    },
    updateAcquisitionConstants(
      draftState,
      action: { payload: { imageId: string; acquisitionConstants: AcquisitionConstants } }
    ) {
      const { imageId, acquisitionConstants } = action.payload;
      const { study, image } = getImageById(draftState, imageId) ?? {};
      if (!image) return;

      const anatomicRegionDescription = getAnatomicRegionFromString(image.anatomicRegion);
      _.forEach(study.images, (draftImage) => {
        if (checkMatchingAnatomicRegionForConstants(anatomicRegionDescription, draftImage)) {
          draftImage.acquisitionConstants = acquisitionConstants;
        }
      });
    },
    imageLoadStart(
      draftState,
      action: {
        payload: {
          studyId?: string;
          imageId: string;
          intl: IntlShape;
          annotations?: ImageAnnotations;
          viewport?: Viewport;
          image_metadata?: ImageMetadata;
          origin?: ImageOrigin;
          operators?: string[];
          anatomicRegion?: string;
          acquisitionTime?: Date;
          acquisitionConstants?: AcquisitionConstants;
          detectorInfo?: AcquiredDetectorInfo;
          isSelected?: boolean;
          isNewImage?: boolean;
          feedback?: Feedbacks;
        };
      }
    ) {
      const { studyId, imageId, intl, ...payload } = action.payload;
      const { annotations, isSelected } = payload;
      const imageLoadPayload = _.omit(payload, ['imageId', 'isSelected']);
      let study = draftState.studies[studyId] as ViewerStudy;
      if (!study) {
        study = getOnlyStudy(draftState);
      }
      if (!study) return;

      let { image } = getImageById(draftState, imageId) ?? {};
      const isExistingImage = !!image;
      const isFirstImage = _.isEmpty(_.flatten(_.map(draftState.studies, 'images')));

      image = _.merge(image, {
        _id: imageId,
        study: study._id,
        isImageLoading: true,
        displayableImage: undefined,
        loadError: undefined,
        predictions: undefined,
        isPredictionsLoading: false,
        isRealSizeMeasurementCalibration: false,
        inferenceError: undefined,
        toolsList: createImageToolsList(() => intl),
        isSelected: isSelected ?? false,
        ...imageLoadPayload,
        isInitialAnnotations: !!annotations,
        PACS: { isSyncOngoing: false },
        saveState: {},
        reloadedState: {
          acquisitionInfo: formatXRayImageMainState(imageLoadPayload),
          viewer: formatXRayImageViewerState(imageLoadPayload),
        },
        ...image,
      });
      if (!isExistingImage) {
        study.images.push(image);
        displayImageInFreeView(draftState, imageId);
      }
      if (isFirstImage || isSelected) {
        selectImage(draftState, imageId);
      }
    },
    imageLoadSuccess(
      draftState,
      action: {
        payload: {
          imageId: string;
          isAIOnly: boolean;
          speciesMapping: object;
        } & LoadedImage;
      }
    ) {
      const {
        imageId,
        imageData,
        dicomData,
        filename,
        isNotProcessable,
        isAIOnly,
        speciesMapping,
      } = action.payload;
      const { image, study } = getImageById(draftState, imageId) ?? {};
      if (!image) return;

      image.isImageLoading = false;
      image.isNotProcessable = isNotProcessable;

      if (dicomData) {
        if (isImageFromAnotherStudy(draftState, imageId, dicomData, { isAIOnly })) {
          image.isImageLoading = true;
          image.isFromAnotherStudy = true;
          return;
        }
        const { detectorInfo, acquisitionTime, patient } = extractAcquisitionDataFromDicomData(
          dicomData,
          speciesMapping
        );

        if (patient.file_id === study.patient?._id) {
          delete patient.file_id;
        }
        if (!study.patient?.name && patient?.name) {
          study.patient = patient;
        }

        image.photometric_interpretation = getDicomDataValue(
          dicomData,
          'PhotometricInterpretation'
        );
        image.dicomData = _.merge(image.dicomData ?? {}, dicomData);
        image.detectorInfo ??= detectorInfo;
        image.acquisitionTime ??= acquisitionTime;

        if (image.isNewImage) {
          image.needRawSave = true;
        }
      }

      image.initialDataImage ??= {
        ...convertDisplayableImageDataToDataImage(imageData),
        rowPixelSpacing: imageData.rowPixelSpacing,
        columnPixelSpacing: imageData.columnPixelSpacing,
      };

      image.displayableImage = injectPixelSpacingIntoDisplayableImage(image, imageData);
      image.loadError = undefined;
      image.filename = filename;
      const { viewport, annotations } = adaptStoredViewportAndAnnotationsToLoadedImage(image);
      image.viewport = viewport;
      image.annotations = annotations;
      applyPixelSpacingFromRealSizeMeasurement(image);

      image.isNewImage = false;
    },
    imageLoadFailure(draftState, action: { payload: { imageId: string; loadError: boolean } }) {
      const { imageId, loadError } = action.payload;
      const image = getImageById(draftState, imageId)?.image;
      if (image) {
        image.isImageLoading = false;
        image.loadError = loadError;
      }
    },
    cacheInitialContent(
      draftState,
      action: { payload: { imageId: string; imageData: DisplayableImageData } }
    ) {
      const { imageId, imageData } = action.payload;
      const image = getImageById(draftState, imageId)?.image;
      if (!image) return;
      image.initialDataImage = {
        ...convertDisplayableImageDataToDataImage(imageData),
        rowPixelSpacing: imageData.rowPixelSpacing,
        columnPixelSpacing: imageData.columnPixelSpacing,
      };
    },
    imageSelect(draftState, action: { payload: { imageId: string } }) {
      selectImage(draftState, action.payload.imageId);
    },
    selectNextWorklistImage(draftState, action: { payload: { imageId: string } }) {
      const { imageId } = action.payload;
      const { image, study } = getImageById(draftState, imageId) ?? {};
      const selectedImagePosition = _.findIndex(study.images, image);
      let nextImageId;
      for (let i = 1; i < study.images.length; i += 1) {
        const currentPosition = (selectedImagePosition + i) % study.images.length;
        const potentialNextImageId = study.images[currentPosition]._id;
        if (isWorkListImage(study.images[currentPosition])) {
          nextImageId = potentialNextImageId;
          break;
        }
      }
      if (nextImageId) selectImage(draftState, nextImageId);
    },
    deleteImage(draftState, action: { payload: { imageId: string } }) {
      const { imageId } = action.payload;
      const { study, image } = getImageById(draftState, imageId) ?? {};
      if (!image) return;

      const deletedImageIndex = study.images.indexOf(image);
      const { viewCount = 1 } = NEGATO_MODE_INFO[draftState.negatoMode as NegatoMode];
      if (image.isSelected && viewCount === 1) {
        const nextImageId = (
          study.images[deletedImageIndex + 1] ?? study.images[deletedImageIndex - 1]
        )?._id;
        if (nextImageId) selectImage(draftState, nextImageId);
      }
      study.images.splice(deletedImageIndex, 1);
    },
    setRawDataSaved(draftState, action: { payload: { imageId: string } }) {
      const { imageId } = action.payload;
      const { image: draftImage } = getImageById(draftState, imageId) ?? {};
      if (draftImage) {
        draftImage.isRawSavedOnce = true;
        draftImage.needRawSave = false;
      }
    },
    processingStart(
      draftState,
      action: {
        payload: { imageId: string; processingType: ProcessingKind | string; isReload?: boolean };
      }
    ) {
      const { imageId, processingType } = action.payload;
      const draftImage = getImageById(draftState, imageId)?.image;

      if (draftImage) {
        draftImage.isProcessingOngoing = true;
        draftImage.image_metadata = _.merge(draftImage.image_metadata ?? {}, {
          processingType,
        });
      }
    },
    processingDone(
      draftState,
      action: {
        payload: {
          imageId: string;
          processedImage?: DataImage;
          isFromLastProcessing?: boolean;
          isNewProcessing?: boolean;
        };
      }
    ) {
      const { imageId, processedImage, isNewProcessing } = action.payload;
      const image = getImageById(draftState, imageId)?.image;
      if (!image) return;

      image.isProcessingOngoing = false;
      image.processedImage = processedImage;
      image.isFromLastProcessing = action.payload.isFromLastProcessing;
      image.isImageNewPredictionsNeeded = isNewProcessing;
      if (isNewProcessing && processedImage) {
        image.viewport = _.merge(image.viewport, {
          invert: false,
          voi: {
            windowCenter: processedImage.windowCenter,
            windowWidth: processedImage.windowWidth,
          },
          uint16Voi: {
            windowCenter: processedImage.windowCenter,
            windowWidth: processedImage.windowWidth,
          },
        });
      }
      const processingRect =
        processedImage?.collimation_rect ??
        convertCropAnnotationsToCropRect(image.annotations?.Crop);
      if (processedImage?.collimation_rect) {
        const [cropUuid = 'crop_uuid'] = Object.keys(image.annotations?.Crop ?? {});
        image.annotations = _.merge(image.annotations ?? {}, {
          Crop: {
            [cropUuid]: {
              ...convertRectToCropHandles(processedImage.collimation_rect),
              uuid: cropUuid,
            },
          },
        });
      }
      if (processingRect) {
        const [x, y, width, height] = processingRect;
        image.image_metadata = {
          ...image.image_metadata,
          processingRect: { x, y, width, height },
        };
      }
    },
    updateProcessingOptions(
      draftState,
      action: { payload: { imageId: string; photometric_interpretation?: string } }
    ) {
      const { imageId, photometric_interpretation } = action.payload;
      const image = getImageById(draftState, imageId)?.image;
      if (image) {
        _.merge(image, { photometric_interpretation });
      }
    },
    enableConfigurableFilterParameters(
      draftState,
      action: {
        payload: {
          imageId: string;
        } & (
          | {
              isEnabled: true;
              processingFilterId?: string;
              processingOptionsProvider: ProcessingOptionsProvider;
            }
          | { isEnabled: false }
        );
      }
    ) {
      const { imageId, isEnabled } = action.payload;
      const image = getImageById(draftState, imageId)?.image;
      if (!image) return;
      image.isFilterFineTuning = isEnabled;
      if (!isEnabled) {
        image.disabledTools = undefined;
        image.fineTunedProcessedImage = undefined;
        image.filterParameters = undefined;
        return;
      }

      const { processingFilterId, processingOptionsProvider } = action.payload;
      const annotationsToolsName = _.difference(
        Object.keys(image.toolsList),
        NON_ANNOTATIONS_TOOLS
      );
      image.disabledTools = annotationsToolsName;
      const filter = processingOptionsProvider.getProcessingOptions(
        processingFilterId ?? image.image_metadata?.processingType,
        image.anatomicRegion
      );
      // This denote that we want to edit the existing processing filter parameters
      if (processingFilterId) {
        image.filterParameters = _.cloneDeep(filter);
      } else {
        // Here we are creating a new fine-tuned processing based on the existing one
        image.filterParameters = {
          parameters: filter.parameters,
        } as FilterDescription;
      }
    },
    updateConfigurableFilterParameters(
      draftState,
      action: { payload: { imageId: string; filterParameters: FilterDescription } }
    ) {
      const { imageId, filterParameters } = action.payload;
      const image = getImageById(draftState, imageId)?.image;
      if (image) {
        _.merge(image, { filterParameters });
      }
    },
    processingFineTuningToggle(
      draftState,
      action: {
        payload: {
          imageId: string;
          isFineTunedProcessingOngoing: boolean;
        };
      }
    ) {
      const { imageId, isFineTunedProcessingOngoing } = action.payload;
      const image = getImageById(draftState, imageId)?.image;
      if (!image) return;

      image.isFineTunedProcessingOngoing = isFineTunedProcessingOngoing;
    },
    updateFinetunedProcessedImage(
      draftState,
      action: {
        payload: {
          imageId: string;
          fineTunedProcessedImage: DisplayableImageData;
        };
      }
    ) {
      const { imageId, fineTunedProcessedImage } = action.payload;
      const image = getImageById(draftState, imageId)?.image;
      if (!image) return;

      image.fineTunedProcessedImage = injectPixelSpacingIntoDisplayableImage(
        image,
        fineTunedProcessedImage
      );
    },
    predictionsLoadStart(draftState, action: { payload: { imageId: string } }) {
      const { imageId } = action.payload;
      const image = getImageById(draftState, imageId)?.image;
      if (image) {
        image.isPredictionsLoading = true;
        image.inferenceError = undefined;
      }
    },
    predictionsLoadSuccess(
      draftState,
      action: { payload: { imageId: string; predictions: AllPredictions } }
    ) {
      const { imageId, predictions } = action.payload;
      const image = getImageById(draftState, imageId)?.image;
      if (!image) return;

      image.isPredictionsLoading = false;
      image.predictions = predictions;
      image.isImageNewPredictionsNeeded = false;
      const precomputedAnnotation = computePrecomputedAnnotations(image);
      if (precomputedAnnotation) {
        image.annotations = _.merge(image.annotations, precomputedAnnotation);
      }
    },
    predictionsLoadFailure(
      draftState,
      action: { payload: { imageId: string; inferenceError: any } }
    ) {
      const { imageId, inferenceError } = action.payload;
      const image = getImageById(draftState, imageId)?.image;
      if (!image) return;

      image.isPredictionsLoading = false;
      image.inferenceError = inferenceError;
    },
    updateFeedback(
      draftState,
      action: { payload: { imageId: string; patternName: string; isPositive: boolean } }
    ) {
      const { imageId, patternName, isPositive } = action.payload;
      const image = getImageById(draftState, imageId)?.image;
      if (!image) return;

      image.feedback = { ...image.feedback, [patternName]: isPositive };
    },
    setActiveRegion(
      draftState,
      action: { payload: { imageId: string; activeRegionName: string } }
    ) {
      const { imageId, activeRegionName } = action.payload;
      const image = getImageById(draftState, imageId)?.image;
      if (!image) return;

      image.activeRegionName = activeRegionName;
    },
    updateToolsStates(draftState, action: { payload: { imageId: string; toolsState: object } }) {
      const { imageId, toolsState } = action.payload;
      const image = getImageById(draftState, imageId)?.image;
      if (!image) return;

      _.merge(image.toolsList, toolsState);
      handleComputedToolsActivation(image, toolsState as ToolsStates);
    },
    updateAnnotation(
      draftState,
      action: { payload: { imageId: string; toolName: string; measurementData: AnyAnnotation } }
    ) {
      const { imageId, toolName, measurementData } = action.payload;
      const image = getImageById(draftState, imageId)?.image;
      if (!image) return;

      image.isAnnotationsSaved = false;
      image.annotations = _.merge(image.annotations, {
        [toolName]: { [measurementData.uuid]: measurementData },
      });
    },
    updateViewport(draftState, action: { payload: { imageId: string; viewport: Viewport } }) {
      const { imageId, viewport } = action.payload;
      const image = getImageById(draftState, imageId)?.image;
      if (!image) return;

      image.viewport = _.merge(image.viewport, viewport);
    },
    removeAnnotation(
      draftState,
      action: { payload: { imageId: string; toolName: string; uuid: string } }
    ) {
      const { imageId, toolName, uuid } = action.payload;
      const image = getImageById(draftState, imageId)?.image;
      if (!image) return;

      image.isAnnotationsSaved = false;
      delete image.annotations?.[toolName as ConfigurableToolsKey]?.[uuid];
    },
    skipInitialAnnotation(draftState, action: { payload: { imageId: string } }) {
      const { imageId } = action.payload;
      const image = getImageById(draftState, imageId)?.image;
      if (!image) return;

      image.isInitialAnnotations = false;
    },
    changeAnnotationsVisibility(
      draftState,
      action: { payload: { imageId: string; showAnnotations: boolean } }
    ) {
      const { imageId, showAnnotations } = action.payload;
      const image = getImageById(draftState, imageId)?.image;
      if (!image?.toolsList) return;

      if (!showAnnotations) {
        if (image.shownAnnotations) return;
        const annotationsToolsName = _.difference(Object.keys(image.toolsList), ALWAYS_SHOWN_TOOLS);
        image.shownAnnotations = _.mapValues(
          _.pick(image.toolsList, annotationsToolsName),
          ({ state }) => ({ state })
        );
        const disabledAnnotationsStates = _.fromPairs(
          annotationsToolsName.map((toolName) => [toolName, { state: 'disabled' }])
        );
        _.merge(image.toolsList, disabledAnnotationsStates);
      } else {
        _.merge(image.toolsList, image.shownAnnotations);
        image.shownAnnotations = undefined;
      }
    },
    switchRealSizeCalibration(
      draftState,
      action: { payload: { imageId: string; isOn?: boolean } }
    ) {
      const { imageId, isOn } = action.payload;
      const image = getImageById(draftState, imageId)?.image;
      if (!image) return;

      updateImageToolsForRealSizeMeasurementCalibration(draftState, image, isOn);
    },
    confirmRealSizeCalibration(draftState, action: { payload: { imageId: string } }) {
      const { imageId } = action.payload;
      const image = getImageById(draftState, imageId)?.image;
      if (!image) return;

      (image.image_metadata ??= {}).realSizeMeasurementImageReference = null;
      applyPixelSpacingFromRealSizeMeasurement(image);
    },
    cancelRealSizeCalibration(draftState, action: { payload: { imageId: string } }) {
      const { imageId } = action.payload;
      const image = getImageById(draftState, imageId)?.image;
      if (!image) return;

      (image.annotations ??= {}).RealSizeMeasurementCalibration =
        image.pendingAnnotations?.RealSizeMeasurementCalibration;
      image.pendingAnnotations = undefined;
    },
    resetRealSizeCalibration(draftState, action: { payload: { imageId: string } }) {
      const { imageId } = action.payload;
      const image = getImageById(draftState, imageId)?.image;
      if (!image) return;

      (image.annotations ??= {}).RealSizeMeasurementCalibration = undefined;
      image.displayableImage = injectPixelSpacingIntoDisplayableImage(
        image,
        image.displayableImage
      );
    },
    useOtherImageCalibration(
      draftState,
      action: { payload: { imageId: string; otherImageId: string } }
    ) {
      const { imageId, otherImageId } = action.payload;
      const image = getImageById(draftState, imageId)?.image;
      const otherImage = getImageById(draftState, otherImageId)?.image;
      if (!image || !otherImage) return;

      if (image.image_metadata?.realSizeMeasurementImageReference === otherImageId) {
        image.image_metadata.realSizeMeasurementImageReference = null;
        image.displayableImage = injectPixelSpacingIntoDisplayableImage(
          image,
          image.displayableImage
        );
      } else {
        (image.image_metadata ??= {}).realSizeMeasurementImageReference = otherImageId;
        copyPixelSpacingFromOtherImage(image.displayableImage, otherImage.displayableImage);
      }
    },
    toggleFreeRotation(draftState, action: { payload: { imageId: string; isOn?: boolean } }) {
      const { imageId, isOn } = action.payload;
      const image = getImageById(draftState, imageId)?.image;
      if (!image) return;

      image.isFreeRotationActive = isOn ?? !image.isFreeRotationActive;
    },
    setRotationAngle(draftState, action: { payload: { imageId: string; angle?: number } }) {
      const { imageId, angle } = action.payload;
      const image = getImageById(draftState, imageId)?.image;
      if (!image || !image.viewport) return;

      image.viewport.rotation = angle;
    },
    pacsStartSync(draftState, action: { payload: { imageId: string } }) {
      const { imageId } = action.payload;
      const image = getImageById(draftState, imageId)?.image;
      if (!image) return;

      image.PACS.isSyncOngoing = true;
    },
    pacsEndSync(
      draftState,
      action: { payload: { imageId: string; syncError?: PACSError | Error } }
    ) {
      const { imageId, syncError } = action.payload;
      const image = getImageById(draftState, imageId)?.image;
      if (!image) return;

      image.PACS.syncError = syncError;
      _.merge(image.image_metadata, { PACS: { isSync: syncError === undefined } });
      image.PACS.isSyncOngoing = false;
    },
    updateDisplayableMetadata(
      draftState,
      action: { payload: { imageId: string; metadata: ImageDisplayableMetadata } }
    ) {
      const { imageId, metadata } = action.payload;
      const image = getImageById(draftState, imageId)?.image;
      if (!image) return;

      image.displayedMetadata = metadata;
    },
    changeNegatoMode(draftState, action: { payload: { negatoMode: NegatoMode } }) {
      const { negatoMode } = action.payload;
      draftState.negatoMode = negatoMode;
      const { viewCount: newViewCount = 1 } = NEGATO_MODE_INFO[negatoMode];
      const images = _.flatten(_.map(draftState.studies, 'images'));
      if (draftState.selectedNegatoView >= newViewCount) {
        draftState.selectedNegatoView = 0;
        const baseViewImageId = _.find(images, { negatoView: 0 })?._id;
        if (baseViewImageId) {
          selectImage(draftState, baseViewImageId);
        }
      }

      const selectedImageId = _.find(images, 'isSelected')?._id;
      if (!selectedImageId) return;

      const imagePosition = _.findIndex(images, { _id: selectedImageId } as ViewerImage);
      for (let posOffset = 1; posOffset < images.length; posOffset++) {
        const imageId = images[(imagePosition + posOffset) % images.length]._id;
        // If the image is selected, we stop
        if (imageId === selectedImageId) break;
        if (displayImageInFreeView(draftState, imageId) === DisplayState.NO_FREE_VIEW) {
          break;
        }
      }
    },
    selectNegatoView(draftState, action: { payload: { viewIndex: number } }) {
      const { viewIndex } = action.payload;
      const images = _.flatten(_.map(draftState.studies, 'images'));
      selectNegatoViewInImages(images, viewIndex);
      draftState.selectedNegatoView = viewIndex;
    },
    switchGeneratorError(
      draftState,
      action: { payload: { imageId: string; isDismissed: boolean } }
    ) {
      const { imageId, isDismissed } = action.payload;
      const image = getImageById(draftState, imageId)?.image;
      if (image) {
        image.isGeneratorErrorDismissed = isDismissed;
      }
    },
    setStudyDefaultAntiScatteringGrid(
      draftState,
      action: { payload: { studyId: string; hasGrid: boolean } }
    ) {
      const { studyId, hasGrid } = action.payload;
      const study = draftState.studies[studyId];
      if (study) {
        study.defaultHasAntiScatteringGrid = hasGrid;
      }
    },
    setImageAntiScatteringGrid(
      draftState,
      action: { payload: { imageId: string; hasGrid: boolean } }
    ) {
      const { imageId, hasGrid } = action.payload;
      const image = getImageById(draftState, imageId)?.image;
      if (image) {
        image.image_metadata = {
          ...image.image_metadata,
          has_anti_scattering_grid: hasGrid,
        };
      }
    },
  },
});

export type ViewerActions = typeof viewerSlice.actions[keyof typeof viewerSlice.actions];

const imagesReducer: typeof viewerSlice['reducer'] = (initialState: ViewerState, action) => {
  const producedState = produce(initialState, (draftState) =>
    viewerSlice.reducer(draftState, action)
  );

  const finalState = produce(producedState, (draftProducedState) => {
    // Check if any referenced pixel spacing changed
    syncPixelSpacingWithReferences(initialState, draftProducedState);
    // Sync anatomic regions to species
    syncAnatomicRegionsToSpecies(initialState, draftProducedState);
    // We check if state change happened to produce updated state to save
    updateSavedState(initialState, draftProducedState);
  });
  return finalState;
};

export const {
  loadStudy,
  updateAnatomicRegion,
  updateOperators,
  addWorkListImage,
  cacheInitialContent,
  redoImage,
  loadWorkListImage,
  receiveAcquiredImage,
  updateAcquisitionConstants,
  imageLoadStart,
  imageLoadSuccess,
  imageLoadFailure,
  imageSelect,
  selectNextWorklistImage,
  deleteImage,
  setRawDataSaved,
  processingStart,
  processingDone,
  updateProcessingOptions,
  predictionsLoadStart,
  predictionsLoadSuccess,
  predictionsLoadFailure,
  updateFeedback,
  setActiveRegion,
  updateToolsStates,
  updateAnnotation,
  updateViewport,
  removeAnnotation,
  skipInitialAnnotation,
  changeAnnotationsVisibility,
  switchRealSizeCalibration,
  confirmRealSizeCalibration,
  cancelRealSizeCalibration,
  resetRealSizeCalibration,
  useOtherImageCalibration,
  pacsStartSync,
  pacsEndSync,
  updateDisplayableMetadata,
  changeNegatoMode,
  selectNegatoView,
  switchGeneratorError,
  updatePatient,
  toggleFreeRotation,
  setRotationAngle,
  enableConfigurableFilterParameters,
  updateConfigurableFilterParameters,
  updateFinetunedProcessedImage,
  processingFineTuningToggle,
  setStudyDefaultAntiScatteringGrid,
  setImageAntiScatteringGrid,
} = viewerSlice.actions;

export default imagesReducer;
